<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :row-data="rowData"
            :column-defs="columnDefs"
            :is-auto-width="true"
            :getRowHeight="() => 42"
            :statusBar="statusBar"
            grid-theme="ag-theme-alpine-partner"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="onDaterangeChange"
            />
            <div class="d-none d-sm-flex align-items-baseline mr-1 mb-1">
                <properties-search-multiselect
                    v-model="filterProperties"
                    variant="primary"
                    :properties="filterPropertyOptions"
                />
            </div>
            <b-button-group class="d-sm-none mr-1">
                <more-filters-dropdown class="damage-notice-dropdown">
                    <template v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                        <properties-search-multiselect
                            class="d-sm-none d-flex"
                            v-model="filterProperties"
                            variant="primary"
                            :properties="filterPropertyOptions"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                    </template>
                </more-filters-dropdown>
            </b-button-group>
        </grid>
    </page>
</template>

<script>
import ActionsCellRenderer from './actions-cell-renderer.vue';
import API from '../../../api';
import { convertBlobResponseToJson, formatResponseError, getFilenameFromResponse } from '../../../utils/api';
import DateRangePicker, {
    nameToRange,
    YESTERDAY,
    TODAY,
    TOMORROW,
    LAST_WEEK,
    THIS_WEEK,
    NEXT_WEEK,
    LAST_MONTH,
    THIS_MONTH,
    NEXT_MONTH,
    THIS_YEAR,
    LAST_YEAR,
} from '../../generic/date-range-picker/date-range-picker.vue';
import { format } from 'date-fns';
import Grid from '../../generic/grid/grid.vue';
import { mapActions, mapGetters } from 'vuex';
import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import Page from '../../generic/page/page.vue';
import PropertiesSearchMultiselect from '../../generic/properties-search-multiselect/properties-search-multiselect.vue';
import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
import ReportSummaryBar from '../report-summary-bar.vue';
import {
    REPORT_DAMAGE_NOTICES_CREATE_WORK_ORDER_ENDPOINT,
    REPORT_DAMAGE_NOTICES_DAMAGE_NOTICE_PDF_ENDPOINT,
    REPORT_DAMAGE_NOTICES_DELETE_DAMAGE_NOTICE_ENDPOINT,
    REPORT_DAMAGE_NOTICES_ENDPOINT,
    REPORT_DAMAGE_NOTICES_PROPERTIES_ENDPOINT,
} from '../../../endpoints';

export default {
    name: 'DamageNoticeReport',
    components: {
        DateRangePicker,
        Grid,
        MoreFiltersDropdown,
        Page,
        PropertiesSearchMultiselect,
        reportSummaryBar: ReportSummaryBar,
        actionsCellRenderer: ActionsCellRenderer,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            busy: false,
            dateRange: nameToRange(THIS_MONTH),
            dateRequestFormat: 'yyyy-MM-dd',
            filterPropertyOptions: [],
            filterPropertyOptionIds: new Set(),
            gridApi: null,
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                TOMORROW,
                LAST_WEEK,
                THIS_WEEK,
                NEXT_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                NEXT_MONTH,
                THIS_YEAR,
                LAST_YEAR,
            ],
            statusBar: null,
            rowData: [],
            title: 'Damage Notices',
            tabs: [
                {
                    title: 'Damage Notices',
                    route: '/app/reports/damage-notices/',
                    active: true,
                },
            ],
        };
    },
    created() {
        this.fetchData();
        this.fetchProperties();
    },
    beforeMount() {
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    mounted() {
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        ...mapGetters('damageNoticeLocalStorage', {
            filterPropertiesStorage: 'properties',
        }),
        columnDefs() {
            return [
                {
                    field: 'tech_reservation.rental_property.id',
                    headerName: 'P. ID',
                    minWidth: 70,
                },
                {
                    field: 'created',
                    headerName: 'Created On',
                    type: 'dateTimeColumn',
                    minWidth: 150,
                    maxWidth: 160,
                },
                {
                    field: 'tech_reservation.meta_reservation.id',
                    headerName: 'Inv. ID',
                    minWidth: 100,
                    cellRenderer: ({ data }) =>
                        '<a style="text-decoration: underline;" href="/app/guest/' +
                        `${data.tech_reservation.meta_reservation.user}/invoices/?reservation=` +
                        `${data.tech_reservation.meta_reservation.id}&tab=housekeeping">` +
                        `${data.tech_reservation.meta_reservation.id}</a>`,
                },
                {
                    field: 'work_order.number',
                    headerName: 'Work Order ID',
                    minWidth: 110,
                    cellRenderer: ({ data }) =>
                        data.work_order
                            ? '<a style="text-decoration: underline;" href="/app/work-orders/list/' +
                                `?work_order_id=${data.work_order.id}">${data.work_order.number}</a>` 
                            : ''
                },
                {
                    field: 'tech_reservation.rental_property.name',
                    headerName: 'Property',
                    minWidth: 200,
                },
                {
                    field: 'description',
                    headerName: 'Description',
                    autoWidthExclude: true,
                },
                {
                    headerName: '',
                    minWidth: 204,
                    maxWidth: 204,
                    autoHeight: true,
                    resizable: false,
                    sortable: false,
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: ({ data, node }) => {
                        return {
                            onCreateWorkOrder: () => {
                                this.onCreateWorkOrder(data, node);
                            },
                            onDeleteDamageNotice: () => {
                                this.onDeleteDamageNotice(data, node);
                            },
                            onPrintDamageNotice: () => {
                                this.onPrintDamageNotice(data.id);
                            },
                        };
                    },
                },
            ];
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        filterProperties: {
            get() {
                return this.filterPropertiesStorage.filter((property) =>
                    this.filterPropertyOptionIds.has(property.property_id)
                );
            },
            set(properties) {
                this.changeFilterPropertiesStorage(properties);
            },
        },
        isMobile() {
            const md = 768;
            return window.innerWidth < md || window.innerHeight < md;
        },
        propertiesRequest() {
            return this.filterPropertiesStorage.map((property) => property.property_id).join(',');
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
    },
    methods: {
        ...mapActions('damageNoticeLocalStorage', {
            changeFilterPropertiesStorage: 'changeProperties',
        }),
        async createWorkOrder(damageNotice, node) {
            const { data } = await API.post(REPORT_DAMAGE_NOTICES_CREATE_WORK_ORDER_ENDPOINT(damageNotice.id));
            const newDamageNotice = JSON.parse(JSON.stringify(damageNotice));
            newDamageNotice.work_order = data;
            node.setData(newDamageNotice);
        },
        async deleteDamageNotice(damageNotice, node) {
            await API.delete(REPORT_DAMAGE_NOTICES_DELETE_DAMAGE_NOTICE_ENDPOINT(damageNotice.id));
            this.gridApi.applyTransaction({
                remove: [node],
            });
        },
        async downloadDamageNotice(id) {
            const response = await API.get(REPORT_DAMAGE_NOTICES_DAMAGE_NOTICE_PDF_ENDPOINT(id), {
                responseType: 'blob',
            });
            saveAs(response.data, getFilenameFromResponse(response));
        },
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(REPORT_DAMAGE_NOTICES_ENDPOINT(), {
                    params: {
                        from_date: this.startDateRequest,
                        to_date: this.endDateRequest,
                        properties: this.propertiesRequest,
                    },
                });
                this.rowData = data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data.' });
            } finally {
                this.busy = false;
            }
        },
        async fetchProperties() {
            try {
                const { data } = await API.get(REPORT_DAMAGE_NOTICES_PROPERTIES_ENDPOINT());
                this.filterPropertyOptions = data;
                this.filterPropertyOptionIds = new Set(data.map((option) => option.property_id));
            } catch (e) {
                this.errorToast({ text: 'Could not fetch properties.' });
            }
        },
        async onCreateWorkOrder(damageNotice, node) {
            try {
                this.busy = true;
                await this.createWorkOrder(damageNotice, node);
                this.successToast({ text: 'Work order successfully created.' });
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not create work order.' });
            } finally {
                this.busy = false;
            }
        },
        onDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
        async onDeleteDamageNotice(data, node) {
            try {
                this.busy = true;
                await this.deleteDamageNotice(data, node);
                this.successToast({ text: 'Damage notice successfully deleted.' });
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not delete damage notice.' });
            } finally {
                this.busy = false;
            }
        },
        async onPrintDamageNotice(id) {
            try {
                this.busy = true;
                await this.downloadDamageNotice(id);
            } catch (e) {
                if (e.response) {
                    e.response.data = await convertBlobResponseToJson(e.response);
                }
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not download damage notice pdf.' });
            } finally {
                this.busy = false;
            }
        },
        onResize() {
            const statusBarComponent = this.gridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance = statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
        },
    },
    watch: {
        filterPropertiesStorage() {
            this.fetchData();
        },
    },
};
</script>

<style lang="scss">
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.damage-notice-dropdown {
    :deep(.buttons-wrapper) {
        width: 100%;
    }
}
</style>
